import React, { useState, useEffect, useCallback } from "react";
import {
  getMissionQuantityIncreaseAPI,
  updateMissionQuantityIncreaseAPI,
} from "../../../../../api/index";
import { convertUTCToKoreaTime } from "../../../../../utils/helpers";
import "./QuantityIncreaseHistoryModal.css";

const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  { value: "", label: "전체" },
  { value: "PENDING", label: "승인대기" },
  { value: "APPROVED", label: "승인" },
  { value: "REJECTED", label: "거절" },
  { value: "CANCELLED", label: "취소" },
  { value: "DONE", label: "적용 완료" },
];

const getStatusLabel = (status) => {
  const map = {
    PENDING: "승인대기",
    APPROVED: "승인",
    REJECTED: "거절",
    CANCELLED: "취소",
    DONE: "적용 완료",
  };
  return map[status] ?? status ?? "-";
};

const QuantityIncreaseHistoryModal = ({ isOpen, onClose, webMemberId }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [filters, setFilters] = useState({
    missionName: "",
    status: "",
    appliedDateStart: "",
    appliedDateEnd: "",
  });

  const fetchData = useCallback(
    async (overridePage = null) => {
      if (!webMemberId || !isOpen) return;

      const currentPage = overridePage !== null ? overridePage : page;
      setLoading(true);
      setError(null);
      try {
        const params = {
          webMemberId: Number(webMemberId),
          page: currentPage,
          pageSize,
        };
        if (filters.missionName?.trim())
          params.missionName = filters.missionName.trim();
        if (filters.status) params.status = filters.status;
        if (filters.appliedDateStart)
          params.appliedDateStart = filters.appliedDateStart;
        if (filters.appliedDateEnd)
          params.appliedDateEnd = filters.appliedDateEnd;

        const response = await getMissionQuantityIncreaseAPI(params);
        const resData = response?.data;
        setData(Array.isArray(resData?.data) ? resData.data : []);
        setTotal(resData?.total ?? 0);
      } catch (err) {
        console.error("수량증가 내역 조회 실패:", err);
        setError(
          err?.response?.data?.msg || "수량증가 내역을 불러오는데 실패했습니다."
        );
        setData([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [
      webMemberId,
      isOpen,
      page,
      pageSize,
      filters.missionName,
      filters.status,
      filters.appliedDateStart,
      filters.appliedDateEnd,
    ]
  );

  useEffect(() => {
    if (isOpen && webMemberId) {
      fetchData();
    }
  }, [isOpen, webMemberId, fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    fetchData(1);
  };

  const handleDelete = async (row) => {
    const id = row?.id;
    if (id == null || row?.status !== "PENDING") return;
    if (!window.confirm("이 수량증가 내역을 취소하시겠습니까?")) return;
    setDeletingId(id);
    try {
      await updateMissionQuantityIncreaseAPI({ id, status: "CANCELLED" });
      await fetchData(page);
    } catch (err) {
      console.error("수량증가 내역 취소 실패:", err);
      const msg = err?.response?.data?.msg || "취소에 실패했습니다.";
      setError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const formatApplyDate = (dateString) => {
    if (!dateString) return "-";
    const korea = convertUTCToKoreaTime(dateString);
    if (!korea) return dateString.slice(0, 10);
    return korea.toISOString().slice(0, 10);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (!isOpen) return null;

  return (
    <div className="qih-modal-overlay" onClick={onClose}>
      <div className="qih-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qih-modal-header">
          <h2 className="qih-modal-title">수량증가 내역</h2>
          <button type="button" className="qih-modal-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        <div className="qih-modal-filters">
          <input
            type="text"
            className="qih-filter-input"
            placeholder="광고명 검색"
            value={filters.missionName}
            onChange={(e) => handleFilterChange("missionName", e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <select
            className="qih-filter-select"
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            className="qih-filter-date"
            value={filters.appliedDateStart}
            onChange={(e) => handleFilterChange("appliedDateStart", e.target.value)}
          />
          <span className="qih-filter-sep">~</span>
          <input
            type="date"
            className="qih-filter-date"
            value={filters.appliedDateEnd}
            onChange={(e) => handleFilterChange("appliedDateEnd", e.target.value)}
          />
          <button type="button" className="qih-filter-btn" onClick={handleSearch}>
            검색
          </button>
        </div>

        <div className="qih-modal-body">
          {loading ? (
            <div className="qih-loading">불러오는 중...</div>
          ) : error ? (
            <div className="qih-error">{error}</div>
          ) : (
            <div className="qih-table-wrap">
              <table className="qih-table">
                <thead>
                  <tr>
                    <th>광고 명</th>
                    <th>접수 날짜</th>
                    <th>적용날짜</th>
                    <th>전 일일 수량</th>
                    <th>신청 일일 수량</th>
                    <th>상태</th>
                    <th className="qih-th-delete">삭제</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="qih-empty">
                        수량증가 내역이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    data.map((row) => (
                      <tr key={row._id || row.id || row.ad_id + "-" + (row.createdAt || "")}>
                        <td className="qih-cell-mission">{row.missionName || "-"}</td>
                        <td>{formatApplyDate(row.createdAt)}</td>
                        <td>{formatApplyDate(row.appliedDate)}</td>
                        <td className="qih-cell-num">
                          {row.beforeTotalQuantity != null
                            ? Number(row.beforeDailyQuantity).toLocaleString()
                            : "-"}
                        </td>
                        <td className="qih-cell-num">
                          {row.newTotalQuantity != null
                            ? Number(row.newDailyQuantity).toLocaleString()
                            : "-"}
                        </td>
                        <td>
                          <span className={`qih-status qih-status-${(row.status || "").toLowerCase()}`}>
                            {getStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="qih-cell-delete">
                          {row.status === "PENDING" ? (
                            <button
                              type="button"
                              className="qih-delete-btn"
                              onClick={() => handleDelete(row)}
                              disabled={deletingId === row.id}
                              title="삭제"
                              aria-label="삭제"
                            >
                              ×
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="qih-modal-footer">
          <div className="qih-pagination">
            <button
              type="button"
              className="qih-page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              이전
            </button>
            <span className="qih-page-info">
              {page} / {totalPages} (총 {total}건)
            </span>
            <button
              type="button"
              className="qih-page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              다음
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuantityIncreaseHistoryModal;
